import { FhirResourceTypes } from '../constants/FhirResourceTypes';
import { FhirBinary } from '../types/FhirBinary';
import moment from 'moment';
import dateUtils from './dateUtils';

const extractAllergyItems = (entries: any): string[] => {
  const activeItems: string[] = [];
  const inactiveItems: string[] = [];

  entries.forEach((entry: any) => {
    const text = entry.resource?.code?.text?.trim();
    if (!text) return;

    const allergyStatus = entry.resource?.clinicalStatus?.coding?.[0]?.code;
    const allergyCriticality = entry.resource?.criticality;
    const lastUpdatedRaw = entry.resource?.meta?.lastUpdated;
    const date =
      lastUpdatedRaw && lastUpdatedRaw.length > 10 && lastUpdatedRaw[10] === 'T'
        ? lastUpdatedRaw.substring(0, 10)
        : lastUpdatedRaw;

    let line = date ? `${date} - ${text}` : text;
    if (allergyCriticality) line += `, criticality: ${allergyCriticality}`;

    const target = allergyStatus === 'active' ? activeItems : inactiveItems;
    if (!target.includes(line)) {
      target.push(line);
    }
  });

  const lines: string[] = [];
  if (activeItems.length) lines.push('Active:', ...activeItems);
  if (inactiveItems.length) lines.push('Inactive:', ...inactiveItems);

  return lines.length ? [lines.join('\n')] : [];
};

const extractClinicalConditions = (entries: any): string[] => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const clinicalText = entry.resource?.code?.text?.trim();
    const onsetRaw = entry.resource?.onsetDateTime;
    const onset =
      onsetRaw && onsetRaw.length > 10 && onsetRaw[10] === 'T'
        ? onsetRaw.substring(0, 10)
        : onsetRaw;

    if (!clinicalText) return;

    const line = onset ? `${onset} - ${clinicalText}` : clinicalText;
    if (!uniqueItems.includes(line)) {
      uniqueItems.push(line);
    }
  });

  return uniqueItems.length ? [uniqueItems.join('\n')] : [];
};

const extractFamilMemberHistoryItems = (entries: any, references: any): string[] => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    const reference = references?.find(
      (r: any) => resource?.patient?.reference === `${FhirResourceTypes.Patient}/${r.data?.id}`
    );

    const historyName = reference?.data?.name?.[0];
    const historyText = historyName ? `${historyName?.given?.[0]} ${historyName?.family}` : '';

    const historyLastUpdated = resource?.meta?.lastUpdated;

    const historyRelationship = resource?.relationship?.text;

    const age = resource?.ageAge
      ? `${resource.ageAge.value} ${resource.ageAge.unit ?? ''}`.trim()
      : resource?.ageRange
      ? `${resource.ageRange.low?.value ?? ''}-${resource.ageRange.high?.value ?? ''} ${
          resource.ageRange.low?.unit ?? ''
        }`.trim()
      : resource?.ageString
      ? resource.ageString
      : undefined;

    const deceasedBoolean = resource?.deceasedBoolean;
    const deceasedAge = resource?.deceasedAge
      ? `${resource.deceasedAge.value} ${resource.deceasedAge.unit ?? ''}`.trim()
      : undefined;
    const deceasedDate = resource?.deceasedDate;
    const deceased = deceasedBoolean
      ? 'yes'
      : deceasedAge
      ? `age: ${deceasedAge}`
      : deceasedDate
      ? `date: ${deceasedDate}`
      : undefined;

    const conditionSummary = resource?.condition
      ?.map((c: any) => {
        const condText = c.code?.text || c.code?.coding?.[0]?.display;

        const onset = c.onsetAge
          ? (() => {
              const unit = c.onsetAge.unit ?? '';
              const ageStr = unit.startsWith(String(c.onsetAge.value))
                ? unit
                : `${c.onsetAge.value} ${unit}`.trim();
              return `onset age: ${ageStr}`;
            })()
          : c.onsetRange
          ? `onset range: ${c.onsetRange.low?.value ?? ''}-${c.onsetRange.high?.value ?? ''} ${
              c.onsetRange.low?.unit ?? ''
            }`.trim()
          : c.onsetPeriod
          ? `onset period: ${c.onsetPeriod.start ?? ''} - ${c.onsetPeriod.end ?? ''}`.trim()
          : c.onsetString
          ? `onset: ${c.onsetString}`
          : undefined;

        return [condText, onset].filter(Boolean).join(', ');
      })
      .filter(Boolean)
      .join('; ');

    if (historyText && !uniqueItems.includes(historyText)) {
      let outputStr = historyText;

      if (historyLastUpdated) {
        const formattedDate = moment(historyLastUpdated).format('YYYY-MM-DD');
        outputStr += ` | date: ${formattedDate}`;
      }
      if (historyRelationship) outputStr += ` | relationship: ${historyRelationship}`;
      if (age) outputStr += ` | age: ${age}`;
      if (deceased) outputStr += ` | deceased: ${deceased}`;
      if (conditionSummary) outputStr += ` | condition: ${conditionSummary}`;

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractMedicationItems = (entries: any) => {
  const strengthRegex =
    /(\d+\.?\d*\s*(MG|MCG|MEQ|ML|UNIT|IU)(\s*\/\s*\d+\.?\d*\s*(MG|MCG|MEQ|ML|UNIT|IU))?)/i;

  const getMedicationStrength = (...candidates: (string | undefined)[]) => {
    for (const candidate of candidates) {
      if (!candidate) {
        continue;
      }

      const match = candidate.match(strengthRegex);
      if (match?.[1]) {
        return match[1].trim();
      }
    }

    return undefined;
  };

  const cleanMedicationName = (name: string) => {
    return name
      .replace(strengthRegex, '')
      .replace(/\s*\([^)]*\)\s*$/g, '')
      .replace(/\s+/g, ' ')
      .replace(/[|,;:\-]+$/g, '')
      .trim();
  };

  const normalizeMedicationName = (name: string) => cleanMedicationName(name).toLowerCase();

  const normalizeMedicationStrength = (strength: string | undefined) => {
    return (strength || '').replace(/\s+/g, '').toLowerCase();
  };

  const truncateDate = (value: string | undefined) =>
    value && value.length > 10 && value[10] === 'T' ? value.substring(0, 10) : value;

  const activeMedicationsByKey = new Map<string, string>();
  const pastMedicationsByKey = new Map<string, string>();

  const setMedicationByKey = (target: Map<string, string>, key: string, output: string) => {
    const existing = target.get(key);
    if (!existing || output.length > existing.length) {
      target.set(key, output);
    }
  };

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    if (
      resource?.resourceType !== FhirResourceTypes.MedicationRequest &&
      resource?.resourceType !== FhirResourceTypes.MedicationStatement
    ) {
      return;
    }

    const medtext = resource?.medicationCodeableConcept?.text?.trim();
    const medCoding = resource?.medicationCodeableConcept?.coding?.[0];
    const medCodingDisplay = medCoding?.display?.trim();
    const medCode = medCoding?.code;
    const medReferenceDisplay = resource?.medicationReference?.display?.trim();
    const medName = medtext || medCodingDisplay || medReferenceDisplay || medCode;
    const medStatus = resource?.status;
    const isActive = medStatus === 'active';

    const dosageEntry = resource?.dosageInstruction?.[0] ?? resource?.dosage?.[0];

    const doseAndRate = dosageEntry?.doseAndRate?.[0];
    const doseValue = doseAndRate?.doseQuantity?.value;
    const doseUnit = doseAndRate?.doseQuantity?.unit;
    const doseQuantity =
      doseValue !== undefined && doseUnit ? `${doseValue} ${doseUnit}` : undefined;

    const sig = dosageEntry?.text?.trim();
    const dosageDisplay = sig || doseQuantity;

    const strength = getMedicationStrength(medCodingDisplay, medtext, medReferenceDisplay);

    const formatPeriod = (period: any) => {
      if (!period?.start) {
        return undefined;
      }
      return period.end && period.end !== period.start
        ? `${truncateDate(period.start)} – ${truncateDate(period.end)}`
        : truncateDate(period.start);
    };

    const periodDate =
      formatPeriod(resource?.effectivePeriod) ??
      formatPeriod(resource?.dispenseRequest?.validityPeriod);

    const medDate =
      periodDate ?? truncateDate(resource?.authoredOn) ?? truncateDate(resource?.dateAsserted);

    if (medName) {
      const nameStrengthMatch = medName.match(strengthRegex);
      const displayName =
        nameStrengthMatch?.index !== undefined
          ? [
              medName
                .slice(0, nameStrengthMatch.index)
                .replace(/\s*\([^)]*\)\s*$/g, '')
                .replace(/\s+/g, ' ')
                .replace(/[|,;:\-]+$/g, '')
                .trim(),
              nameStrengthMatch[1].trim()
            ]
              .filter(Boolean)
              .join(' ')
          : [cleanMedicationName(medName), strength].filter(Boolean).join(' ');

      let line = medDate ? `${medDate} - ` : '';
      line += displayName;
      if (dosageDisplay) line += `, ${dosageDisplay}`;

      const dedupeKey = `${normalizeMedicationName(medName)}|${normalizeMedicationStrength(
        strength
      )}`;
      setMedicationByKey(isActive ? activeMedicationsByKey : pastMedicationsByKey, dedupeKey, line);
    }
  });

  const result: string[] = [];
  if (activeMedicationsByKey.size) {
    result.push(`Active Medication:\n${Array.from(activeMedicationsByKey.values()).join('\n')}`);
  }
  if (pastMedicationsByKey.size) {
    result.push(`Past Medication:\n${Array.from(pastMedicationsByKey.values()).join('\n')}`);
  }

  return result;
};

const extractDefaultItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const text = entry.resource?.code?.text?.trim();
    if (text && !uniqueItems.includes(text)) {
      uniqueItems.push(text);
    }
  });

  return uniqueItems;
};

const ALLOWED_OBSERVATION_CATEGORIES = [
  'laboratory',
  'imaging',
  'social-history',
  'alcohol-use',
  'tobacco-use'
];

const OBSERVATION_CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  laboratory: 'Laboratory/Pathology',
  imaging: 'Radiology',
  'social-history': 'Social History',
  'alcohol-use': 'Alcohol Use',
  'tobacco-use': 'Tobacco Use'
};

const convertBrToNewlines = (text: string): string => {
  return text.replace(/\\\.?br\\?/g, '\n');
};

const extractObservationItems = (entries: any) => {
  const categoryItems = new Map<string, string[]>();

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    if (/^rad-\d+$/i.test(resource?.id ?? '')) {
      return;
    }

    const categoryCode =
      resource?.category?.[0]?.coding?.[0]?.code?.toLowerCase() ||
      resource?.category?.[0]?.text?.toLowerCase();

    if (!categoryCode || !ALLOWED_OBSERVATION_CATEGORIES.includes(categoryCode)) {
      return;
    }

    const obsDisplayText =
      resource?.code?.text?.trim() || resource?.code?.coding?.[0]?.display?.trim();

    const effectiveRaw =
      resource?.effectiveDateTime ||
      (resource?.effectivePeriod
        ? `${resource.effectivePeriod.start ?? ''} - ${resource.effectivePeriod.end ?? ''}`.trim()
        : undefined);

    // Truncate datetime to date-only (YYYY-MM-DD)
    const effective =
      effectiveRaw && effectiveRaw.length > 10 && effectiveRaw[10] === 'T'
        ? effectiveRaw.substring(0, 10)
        : effectiveRaw;

    const rawValue = resource?.valueQuantity
      ? `${resource.valueQuantity.value} ${resource.valueQuantity.unit ?? ''}`.trim()
      : resource?.valueCodeableConcept
      ? resource.valueCodeableConcept.text || resource.valueCodeableConcept.coding?.[0]?.display
      : resource?.valueString
      ? resource.valueString
      : resource?.valueBoolean !== undefined
      ? `${resource.valueBoolean}`
      : resource?.valueInteger !== undefined
      ? `${resource.valueInteger}`
      : resource?.valueRange
      ? `${resource.valueRange.low?.value ?? ''}-${resource.valueRange.high?.value ?? ''} ${
          resource.valueRange.low?.unit ?? ''
        }`.trim()
      : resource?.valueRatio
      ? `${resource.valueRatio.numerator?.value ?? ''}/${
          resource.valueRatio.denominator?.value ?? ''
        }`
      : undefined;

    const value = rawValue ? convertBrToNewlines(rawValue) : undefined;

    if (!obsDisplayText && !value) return;

    let line = effective ? `${effective} - ` : '';
    if (obsDisplayText) line += obsDisplayText;
    if (value) line += `${obsDisplayText ? ', ' : ''}${value}`;

    const categoryName =
      OBSERVATION_CATEGORY_DISPLAY_NAMES[categoryCode] ||
      resource?.category?.[0]?.text?.trim() ||
      categoryCode;

    if (!categoryItems.has(categoryName)) {
      categoryItems.set(categoryName, []);
    }
    const items = categoryItems.get(categoryName)!;
    if (!items.includes(line)) {
      items.push(line);
    }
  });

  const result: string[] = [];
  categoryItems.forEach((items, categoryName) => {
    result.push(`${categoryName}\n${items.join('\n')}`);
  });

  return result;
};

const extractImmunizationItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    const immText = resource?.vaccineCode?.text?.trim();

    const occurrenceRaw = resource?.occurrenceDateTime || resource?.occurrenceString;
    const occurrence =
      occurrenceRaw && occurrenceRaw.length > 10 && occurrenceRaw[10] === 'T'
        ? occurrenceRaw.substring(0, 10)
        : occurrenceRaw;

    const immDoseQuantity = resource?.doseQuantity
      ? `${resource.doseQuantity.value} ${resource.doseQuantity.unit ?? ''}`.trim()
      : undefined;

    if (!immText) return;

    let line = occurrence ? `${occurrence} - ` : '';
    line += immText;
    if (immDoseQuantity) line += `, ${immDoseQuantity}`;

    if (!uniqueItems.includes(line)) {
      uniqueItems.push(line);
    }
  });

  return uniqueItems;
};

const extractProcedureItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const procResource = entry.resource;

    const getCodingText = (obj: any) =>
      obj?.text || obj?.coding?.[0]?.display || obj?.coding?.[0]?.code;

    const formatDate = (value: any) => {
      if (!value) return '';
      const match = String(value).match(/\d{4}-\d{2}-\d{2}/);
      return match ? match[0] : String(value);
    };

    const text = procResource?.code?.coding?.[0]?.text?.trim() || getCodingText(procResource?.code);

    const performed = formatDate(
      procResource?.performedDateTime ||
        procResource?.performedPeriod?.start ||
        procResource?.performedString
    );

    const outcome = getCodingText(procResource?.outcome);

    const complication = procResource?.complication?.map(getCodingText).filter(Boolean).join(', ');

    if (text) {
      const summary = complication || outcome || 'no complications';

      const outputStr = `${performed ? `${performed} - ` : ''}${text}, ${summary}`;

      if (!uniqueItems.includes(outputStr)) {
        uniqueItems.push(outputStr);
      }
    }
  });

  return uniqueItems;
};

const extractDiagnosticReportItems = (entries: any) => {
  const items: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    if (!/^rad-\d+$/i.test(resource?.id ?? '')) {
      return;
    }

    const note = resource?.valueString;
    if (!note) return;

    const effectiveRaw = resource?.effectiveDateTime;

    const effective =
      effectiveRaw && effectiveRaw.length > 10 && effectiveRaw[10] === 'T'
        ? effectiveRaw.substring(0, 10)
        : effectiveRaw;

    const body = convertBrToNewlines(note)
      .split('\n')
      .map((bodyLine: string) => bodyLine.trim())
      .filter(Boolean)
      .join('\n');

    if (!body) return;

    const block = effective ? `${effective}\n${body}` : body;

    if (!items.includes(block)) {
      items.push(block);
    }
  });

  if (items.length === 0) {
    return [];
  }

  return ['Laboratory/Pathology', ...items];
};

const extractEncounterItems = (entries: any) => {
  const uniqueItems: string[] = [];

  entries.forEach((entry: any) => {
    const resource = entry.resource;

    const getCodingText = (obj: any) =>
      obj?.text || obj?.coding?.[0]?.display || obj?.coding?.[0]?.code;

    const getReferenceText = (ref: any) => ref?.display || ref?.reference;

    const text =
      resource?.type?.map(getCodingText).filter(Boolean).join(', ') ||
      getCodingText(resource?.serviceType) ||
      resource?.class?.display ||
      resource?.class?.code;

    const status = resource?.status;

    const classCode =
      resource?.class?.display || resource?.class?.code
        ? `${resource?.class?.display ?? resource?.class?.code}`
        : undefined;

    const type = resource?.type?.map(getCodingText).filter(Boolean).join(', ');

    const serviceType = getCodingText(resource?.serviceType);

    const priority = getCodingText(resource?.priority);

    const basedOn = resource?.basedOn?.map(getReferenceText).filter(Boolean).join(', ');

    const length = resource?.length
      ? `${resource.length.value ?? ''} ${resource.length.unit ?? ''}`.trim()
      : undefined;

    const reasonCode = resource?.reasonCode?.map(getCodingText).filter(Boolean).join(', ');

    const reasonReference = resource?.reasonReference
      ?.map(getReferenceText)
      .filter(Boolean)
      .join(', ');

    const diagnosis = resource?.diagnosis
      ?.map((d: any) => getReferenceText(d.condition))
      .filter(Boolean)
      .join(', ');

    const hospitalization = resource?.hospitalization
      ? [
          getCodingText(resource.hospitalization.admitSource),
          getCodingText(resource.hospitalization.dischargeDisposition)
        ]
          .filter(Boolean)
          .join(', ')
      : undefined;

    const participant = resource?.participant
      ?.map((p: any) => getReferenceText(p.individual))
      .filter(Boolean)
      .join(', ');

    const serviceProvider = getReferenceText(resource?.serviceProvider);

    const period = resource?.period?.start
      ? resource.period.end && resource.period.end !== resource.period.start
        ? `${resource.period.start} – ${resource.period.end}`
        : resource.period.start
      : undefined;

    if (text && !uniqueItems.includes(text)) {
      let outputStr = text;

      if (status) outputStr += ` | status: ${status}`;
      if (classCode) outputStr += ` | class: ${classCode}`;
      if (type) outputStr += ` | type: ${type}`;
      if (serviceType) outputStr += ` | serviceType: ${serviceType}`;
      if (priority) outputStr += ` | priority: ${priority}`;
      if (basedOn) outputStr += ` | basedOn: ${basedOn}`;
      if (length) outputStr += ` | length: ${length}`;
      if (reasonCode) outputStr += ` | reasonCode: ${reasonCode}`;
      if (reasonReference) outputStr += ` | reasonReference: ${reasonReference}`;
      if (diagnosis) outputStr += ` | diagnosis: ${diagnosis}`;
      if (hospitalization) outputStr += ` | hospitalization: ${hospitalization}`;
      if (participant) outputStr += ` | participant: ${participant}`;
      if (serviceProvider) outputStr += ` | serviceProvider: ${serviceProvider}`;
      if (period) outputStr += ` | period: ${period}`;

      uniqueItems.push(outputStr);
    }
  });

  return uniqueItems;
};

const extractItems = (entries: any, reportType: string, references: any) => {
  switch (reportType) {
    case FhirResourceTypes.Encounter:
      return extractEncounterItems(entries);
    case FhirResourceTypes.AllergyIntolerance:
      return extractAllergyItems(entries);
    case FhirResourceTypes.Condition:
      return extractClinicalConditions(entries);
    case FhirResourceTypes.FamilyMemberHistory:
      return extractFamilMemberHistoryItems(entries, references);
    case FhirResourceTypes.MedicationRequest:
    case FhirResourceTypes.MedicationStatement:
      return extractMedicationItems(entries);
    case FhirResourceTypes.Observation:
      return extractObservationItems(entries);
    case FhirResourceTypes.Immunization:
      return extractImmunizationItems(entries);
    case FhirResourceTypes.Procedure:
      return extractProcedureItems(entries);
    case FhirResourceTypes.DiagnosticReport:
      return extractDiagnosticReportItems(entries);
    default:
      return extractDefaultItems(entries);
  }
};

const processBinaryData = (
  data: any,
  setBinaryItems: React.Dispatch<React.SetStateAction<FhirBinary[]>>
) => {
  const entries = data?.entry ?? [];
  if (!entries || entries.length === 0) return;

  const unqiueBinaryItems: FhirBinary[] = [];
  if (!unqiueBinaryItems) return;

  entries.forEach((entry: any) => {
    if (entry?.resource?.contained?.length > 0) {
      entry?.resource?.contained.forEach((resource: FhirBinary) => {
        if (resource.resourceType === FhirResourceTypes.Binary) {
          unqiueBinaryItems.push(resource);
        }
      });
    }
  });

  if (unqiueBinaryItems?.length > 0) {
    setBinaryItems((prev) => {
      const existingIds = new Set(prev.map((item) => item.id));
      const newItems = unqiueBinaryItems.filter((item) => !existingIds.has(item.id));
      return newItems.length > 0 ? [...prev, ...newItems] : prev;
    });
  }
};

const processFhirBundleResource = (
  data: any,
  setContent: React.Dispatch<React.SetStateAction<string>>,
  reportType: string,
  setSelectedClinicalReports: React.Dispatch<React.SetStateAction<string[]>>,
  setBinaryItems: React.Dispatch<React.SetStateAction<FhirBinary[]>>,
  references: any
) => {
  const entries = data?.entry ?? [];
  if (!entries.length) return;

  const uniqueItems: string[] | undefined = extractItems(entries, reportType, references);

  //processBinaryData(data, setBinaryItems);

  if (!uniqueItems || uniqueItems?.length === 0) return;

  setSelectedClinicalReports((prev) => (prev.includes(reportType) ? prev : [...prev, reportType]));

  if (uniqueItems?.length > 0) {
    setContent((prev) => {
      const existing = prev
        .split('\n\n')
        .map((s) => s.trim())
        .filter(Boolean);

      if (reportType === FhirResourceTypes.DiagnosticReport) {
        const merged = [...existing];
        uniqueItems.forEach((item) => {
          if (!merged.includes(item)) {
            merged.push(item);
          }
        });
        return merged.join('\n\n');
      }

      const merged = [...existing];
      uniqueItems.forEach((item) => {
        const newlineIdx = item.indexOf('\n');
        if (newlineIdx !== -1) {
          const header = item.substring(0, newlineIdx);
          const newLines = item
            .substring(newlineIdx + 1)
            .split('\n')
            .filter(Boolean);
          const existingIdx = merged.findIndex(
            (block) => block.startsWith(header + '\n') || block === header
          );
          if (existingIdx !== -1) {
            const existingLines = merged[existingIdx].split('\n');
            newLines.forEach((line) => {
              if (!existingLines.includes(line)) {
                existingLines.push(line);
              }
            });
            merged[existingIdx] = existingLines.join('\n');
          } else {
            merged.push(item);
          }
        } else {
          if (!merged.includes(item)) {
            merged.push(item);
          }
        }
      });

      return merged.join('\n\n');
    });
  }
};

const processFhirResource = (
  data: any,
  setContent: React.Dispatch<React.SetStateAction<string>>,
  reportType: string,
  setSelectedClinicalReports: React.Dispatch<React.SetStateAction<string[]>>,
  setBinaryItems: React.Dispatch<React.SetStateAction<FhirBinary[]>>
) => {
  if (!data) return;

  const text = data?.code?.text?.trim();
  if (!text) return;

  const unqiueBinaryItems: FhirBinary[] = [];
  if (data?.contained?.length > 0) {
    data?.contained.forEach((resource: FhirBinary) => {
      if (resource.resourceType === FhirResourceTypes.Binary) {
        unqiueBinaryItems.push(resource);
      }
    });
  }

  setSelectedClinicalReports((prev) => {
    return prev.includes(reportType) ? prev : [...prev, reportType];
  });

  setContent((prev) => {
    const existing = prev
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    if (existing.includes(text)) return prev;

    return [...existing, text].join('\n');
  });

  // if (unqiueBinaryItems?.length > 0) {
  //   setBinaryItems((prev) => {
  //     const existingIds = new Set(prev.map((item) => item.id));
  //     const newItems = unqiueBinaryItems.filter((item) => !existingIds.has(item.id));
  //     return newItems.length > 0 ? [...prev, ...newItems] : prev;
  //   });
  // }
};

const extractPatientResourceRefs = (data: any) => {
  if (!data) return;
  const refs: string[] = [];

  const entries = data?.entry ?? [];
  if (!entries.length) return;

  entries.forEach((entry: any) => {
    if (entry?.resource?.patient) {
      refs.push(entry.resource.patient.reference);
    }
  });

  return refs;
};

const processDocumentReferenceResource = (data: any): FhirBinary => {
  const attachment = data?.content?.[0]?.attachment;

  return {
    contentType: attachment?.contentType,
    data: attachment?.url,
    id: data?.id,
    resourceType: 'Binary'
  };
};

const FhirResourceUtils = {
  processFhirBundleResource,
  processFhirResource,
  extractPatientResourceRefs,
  processDocumentReferenceResource
};

export default FhirResourceUtils;
